import type { I18nDictionaryShape } from '../../types';
import type { featuresEnGb } from '../en-GB/features';

export const featuresRoRo = {
  annotations: {
    aiFeedbackAlert: {
      desktopPrompt: 'Dă clic dreapta pe fotografie pentru a trimite feedback AI!',
      improveAccuracy: 'Îmbunătățește acuratețea.',
      improveAccuracyFor: 'Îmbunătățește acuratețea pentru {{tradeCompanyName}}.',
      mobilePrompt: 'Apasă lung pe fotografie pentru a trimite feedback AI!',
    },
    aiFeedbackSwitch: {
      label: 'Afișează feedbackul AI',
    },
    aiFilters: {
      analysisFailedDescription:
        'Echipa noastră va investiga. Între timp, continuă cu restul fotografiilor din raport.',
      analysisFailedTitle: 'AI nu a putut analiza această fotografie',
      description: 'Afișează sau ascunde contururile materialelor și compară-le cu fotografia originală.',
      noFiltersDescription:
        'AI-ul Safi conturează doar materialele care nu sunt vizate (precum impuritățile sau atașamentele). Această fotografie conține doar material vizat (de ex. UBC sau PET), așa că nu există contururi sau filtre de afișat.',
      noFiltersTitle: 'Niciun filtru de afișat, deoarece s-a detectat 100% material vizat',
      showOutlines: 'Afișează contururile AI',
      title: 'Ajustează filtrele AI',
    },
    commentBubble: {
      actions: {
        deleteFeedback: 'Șterge feedbackul',
        edit: 'Editează',
      },
      adminMeta: 'ID:{{commentId}} AI:{{aiVersion}}',
      deleteDialog: {
        confirm: 'Șterge',
        message: 'Sigur vrei să ștergi acest feedback? Această acțiune nu poate fi anulată.',
        title: 'Șterge feedbackul',
      },
    },
    commentFormDrawer: {
      actions: {
        cancel: 'Anulează',
        save: 'Salvează',
      },
      feedbackType: {
        incorrectDetection: 'Obiect detectat incorect',
        missingDetection: 'Obiect nedetectat',
      },
      labels: {
        note: 'Notă',
      },
      titleCreate: 'Trimite feedback AI',
      titleEdit: 'Editează feedbackul AI',
    },
    helpAndFeedbackBar: {
      downloadFullImageAriaLabel: 'Descarcă imaginea completă a dovezii',
    },
    segmentationOverlay: {
      ariaLabel: 'Imagine cu suprapunere de segmentare',
      helpText: {
        desktop: 'Treci cu mouse-ul peste fotografie pentru a mări',
        mobile: 'Mărește cu degetele',
      },
    },
    speechBubbleAvatar: {
      viewCommentAriaLabel: 'Vezi comentariul de la {{fullName}}',
    },
  },
  auth: {
    companySignUpForm: {
      actions: {
        next: 'Continuă',
      },
      labels: {
        companyType: 'Tip de companie',
        country: 'Țară',
        name: 'Nume',
      },
      notifications: {
        errorSigningUp: 'Eroare la înregistrare',
      },
      validation: {
        companyTypeRequired: 'Tipul companiei este obligatoriu',
        countryRequired: 'Țara este obligatorie',
        nameRequired: 'Numele este obligatoriu',
      },
    },
    otpForm: {
      actions: {
        signIn: 'Autentifică-te',
      },
      labels: {
        passcode: 'Cod de acces',
      },
      validation: {
        digitsOnly: 'Trebuie să conțină doar cifre',
        passcodeLength: 'Codul de unică folosință trebuie să aibă 4 cifre',
        passcodeRequired: 'Codul de unică folosință este obligatoriu',
      },
    },
    signInEmailForm: {
      actions: {
        next: 'Continuă',
      },
      labels: {
        email: 'E-mail',
      },
      links: {
        signInWithPhone: 'Autentificare cu numărul de telefon',
      },
      validation: {
        emailRequired: 'E-mailul este obligatoriu',
        invalidEmail: 'E-mail invalid',
        termsRequired: 'Trebuie să accepți termenii și condițiile',
      },
    },
    signInPage: {
      subtitle:
        'Autentifică-te în {{brandName}} cu e-mailul de serviciu sau cu numărul de telefon. Îți trimitem un cod de unică folosință pentru acces mai rapid.',
      title: 'Controlul calității cu încredere',
    },
    signInPhoneForm: {
      actions: {
        next: 'Continuă',
        signInWithEmail: 'Autentificare cu e-mail',
      },
      labels: {
        phoneNumber: 'Număr de telefon',
      },
      validation: {
        phoneNumberRequired: 'Numărul de telefon este obligatoriu',
      },
    },
    termsAndPolicyAgreement: {
      fullSentence:
        'Accept <termsLink>Termenii și condițiile</termsLink> și <privacyLink>Politica de confidențialitate</privacyLink> prin alăturarea la {{brandName}}.',
    },
    userSignUpForm: {
      actions: {
        next: 'Continuă',
      },
      labels: {
        email: 'E-mail',
        fullName: 'Nume complet',
        phoneNumber: 'Număr de telefon',
      },
      notifications: {
        errorSigningUp: 'Eroare la înregistrare',
      },
      validation: {
        emailRequired: 'E-mailul este obligatoriu',
        fullNameRequired: 'Numele complet este obligatoriu',
        invalidEmail: 'E-mail invalid',
        phoneNumberRequired: 'Numărul de telefon este obligatoriu',
        termsRequired: 'Trebuie să accepți termenii și condițiile',
      },
    },
  },
  backlog: {
    breakdowns: {
      evaluatedSpecsItems: {
        ungroupedMaterials: 'Materiale negrupate',
      },
      formatDirtScore: {
        average: 'Mediu',
        clean: 'Curat',
        dirty: 'Murdar',
        notAvailable: 'N/A',
        veryClean: 'Foarte curat',
      },
      materialAiBreakdown: {
        aiModelNotSupported: 'Model AI neacceptat',
      },
      materialLabels: {
        aluminium: 'Aluminiu',
        aluminiumAerosol: 'Doză aerosol din aluminiu',
        aluminiumFoodCan: 'Conservă alimentară din aluminiu',
        brown: 'Maro',
        clear: 'Transparent',
        clearFullSleeveNonFoodGrade: 'Transparent cu manșon integral, nealimentar',
        clearNonFoodGrade: 'Transparent, nealimentar',
        clearPetTray: 'Caserolă PET transparentă',
        darkBlue: 'Albastru închis',
        dirtyBottles: 'Sticle murdare',
        expandedPolystyrene: 'Polistiren expandat',
        film: 'Folie',
        foilAndTray: 'Folie și caserolă',
        fullSleeve: 'Manșon integral',
        green: 'Verde',
        hdpe: 'HDPE',
        hdpeBlack: 'Negru',
        hdpeBlue: 'Albastru',
        hdpeBrown: 'Maro',
        hdpeGreen: 'Verde',
        hdpeJazz: 'Jazz',
        hdpeNaturalFoodGrade: 'Natural alimentar',
        hdpeNaturalNonFoodGrade: 'Natural nealimentar',
        hdpeOrange: 'Portocaliu',
        hdpePurple: 'Mov',
        hdpeRed: 'Roșu',
        hdpeSiliconTubes: 'Tuburi din silicon',
        hdpeWhiteFoodGrade: 'Alb alimentar',
        hdpeWhiteNonFoodGrade: 'Alb nealimentar',
        hdpeYellow: 'Galben',
        lightBlue: 'Albastru deschis',
        metal: 'Metal',
        nonAluminium: 'Non-aluminiu',
        nonHdpe: 'Non-HDPE',
        nonMetal: 'Non-metal',
        nonPet: 'Non-PET',
        nonPlastic: 'Non-plastic',
        oilBottle: 'Sticlă de ulei',
        opaque: 'Opac',
        other: 'Altele',
        otherAluminium: 'Alt aluminiu',
        otherColour: 'Altă culoare',
        otherMetal: 'Alt metal',
        otherPlastic: 'Alt plastic',
        pet: 'PET',
        petTrayContaminatedClear: 'Caserolă PET transparentă contaminată',
        polypropylene: 'PP',
        prohibitive: 'Interzis',
        total: 'Total',
        ubc: 'UBC',
        waste: 'Deșeuri',
        white: 'Alb',
      },
      representativeEvidenceFilter: {
        label: 'Vezi imagini cu:',
        options: {
          bestBales: 'Cele mai bune baloturi',
          typicalBales: 'Baloturi tipice',
          worstBales: 'Cele mai slabe baloturi',
        },
      },
      representativeEvidenceSection: {
        distributionTitle: 'Distribuția {{targetName}}',
      },
      specificationAlerts: {
        groupDescription:
          '<boldAction>{{actionText}}</boldAction> - Grupul <boldName>{{name}}</boldName> este <boldComparison>{{comparison}}</boldComparison>',
        groupDescriptionWithoutComparison:
          '<boldAction>{{actionText}}</boldAction> - Grupul <boldName>{{name}}</boldName> este',
        groupIs: 'grupul este',
        the: '- Grupul',
        title: 'Alerte',
      },
      totalWeightWarning: {
        message:
          'Atenție! Greutatea totală ({{formattedTotalWeight}}kg) nu este egală cu suma greutăților din defalcare ({{formattedSum}}kg).',
      },
    },
    cameraGuidance: {
      useCameraStream: {
        notifications: {
          cannotUseCamera: 'Camera nu poate fi utilizată',
          refreshAndAllowCamera:
            'Reîmprospătează pagina și încearcă din nou. Permite accesul la cameră când ți se cere.',
        },
      },
    },
    common: {
      noData: 'Fără date',
    },
    dealDetails: {
      fields: {
        destination: 'Destinație',
        endProcessor: 'Procesator final',
        inspections: 'Inspecții',
        loadingSite: 'Punct de încărcare',
        loads: 'Încărcături',
        material: 'Material',
        producer: 'Producător',
        risk: 'Risc',
      },
      header: {
        deal: 'Tranzacția {{dealId}}',
      },
    },
    dealRevenueDetails: {
      columns: {
        description: 'Descriere',
        id: 'ID',
        invoiceStatus: 'Stare factură',
        invoiceXeroAmountPaid: 'Sumă plătită factură Xero',
        invoiceXeroFullyPaidOn: 'Factură Xero achitată integral la',
        invoiceXeroId: 'ID factură Xero',
        lineTotal: 'Total linie',
        quantity: 'Cantitate',
        unitAmount: 'Preț unitar',
      },
    },
    documentGeneration: {
      notifications: {
        failedToSaveFile: 'Salvarea fișierului a eșuat',
        failedToUploadFile: 'Încărcarea fișierului a eșuat',
      },
    },
    evidence: {
      bulkDownload: {
        empty: 'Nicio dovadă de descărcat',
        error: 'Descărcarea dovezilor a eșuat',
        inProgress: 'Se pregătește descărcarea dovezilor…',
        partialSuccess: 'S-au descărcat {{downloaded}} din {{total}} fotografii',
        success: 'S-au descărcat {{count}} fotografii',
      },
      common: {
        editSample: 'Editează proba',
        manageInspection: 'Gestionează inspecția',
      },
      evidencePhotosViewer: {
        bulkDeleteError: 'Ștergerea fotografiilor a eșuat',
        bulkDeleteSuccess: 'S-au șters {{count}} fotografii',
        noEvidenceFound: 'Nu s-au găsit fotografii',
      },
      loadingPhotoEvidenceGallery: {
        noOtherImagesAvailable: 'Nu există alte imagini disponibile.',
      },
      manualBreakdownFormFields: {
        kilogramUnit: 'Kg',
      },
      sessionSummary: {
        anonymous: 'Anonim',
        withOrigin: '{{origin}} - Încărcat de {{uploadedBy}}',
        withoutOrigin: 'Încărcat de {{uploadedBy}}',
      },
      uploadMenu: {
        downloadAllEvidence: 'Descarcă toate dovezile',
        downloadingAllEvidence: 'Se descarcă…',
        editEvidence: 'Editează dovezile',
        menuAriaLabel: 'Mai multe acțiuni',
        offlineModeComingSoon: 'Modul offline va fi disponibil în curând',
        openCamera: 'Deschide camera',
        uploadEvidence: 'Încarcă dovezi',
      },
      uploadOptionsButton: {
        actions: {
          upload: 'Încarcă',
        },
        options: {
          openCamera: 'Deschide camera',
          uploadPhoto: 'Încarcă fotografie',
        },
      },
    },
    evidenceGrid: {
      columns: {
        evidence: 'Dovezi',
      },
    },
    externalLoad: {
      common: {
        supplier: 'Furnizor',
      },
    },
    findTicketsTable: {
      columns: {
        category: 'Categorie',
        status: 'Stare',
        summary: 'Rezumat',
        team: 'Echipă',
      },
    },
    firmQuoteBulkForm: {
      columns: {
        bunkerAdjustmentFactorCost: 'Cost factor de ajustare bunker',
        bunkerAdjustmentFactorCurrency: 'Monedă factor de ajustare bunker',
        carrier: 'Transportator',
        containerType: 'Tip container',
        demandIncoterm: 'Incoterm cerere',
        destinationCountry: 'Țara de destinație',
        destinationLat: 'Lat. destinație',
        destinationLon: 'Long. destinație',
        destinationName: 'Nume destinație',
        directLandCost: 'Cost transport terestru direct',
        directLandCurrency: 'Monedă transport terestru direct',
        e2eCost: 'Cost E2E',
        e2eCurrency: 'Monedă E2E',
        estimatedDeparture: 'Plecare est.',
        expiresOn: 'Expiră la',
        forwarder: 'Expeditor',
        notes: 'Note',
        oceanFreightCost: 'Cost transport maritim',
        oceanFreightCurrency: 'Monedă transport maritim',
        onCarriageCost: 'Cost transport final',
        onCarriageCurrency: 'Monedă transport final',
        originCountry: 'Țara de origine',
        originLat: 'Lat. origine',
        originLon: 'Long. origine',
        originName: 'Nume origine',
        peakSeasonSurchargeCost: 'Cost suprataxă sezon de vârf',
        peakSeasonSurchargeCurrency: 'Monedă suprataxă sezon de vârf',
        portOfDischarge: 'POD',
        portOfLoading: 'POL',
        preCarriageCost: 'Cost transport inițial',
        preCarriageCurrency: 'Monedă transport inițial',
        quotedOn: 'Cotat la',
        supplyIncoterm: 'Incoterm ofertă',
        typeOfGoods: 'Tip de marfă',
      },
    },
    grade: {
      compositionReport: {
        annotatedBaleAlt: 'Balot adnotat',
      },
      imageQualityPrompt: {
        label: 'Refă fotografia',
      },
    },
    groundControl: {
      common: {
        descriptions: {
          calculateDealProfitability: 'Calculează profitabilitatea tranzacției',
          createPurchaseOrderAndSendToSeller: 'Creează o comandă de achiziție și trimite-o vânzătorului spre semnare',
          createSalesOrderAndSendToBuyer: 'Creează o comandă de vânzare și trimite-o cumpărătorului spre semnare',
          viewEditAndMatch: 'Vizualizează, editează și potrivește',
        },
        labels: {
          createPurchaseOrder: 'Creează comandă de achiziție',
          createSalesOrder: 'Creează comandă de vânzare',
          dealProfitabilityCalculator: 'Calculator de profitabilitate a tranzacției',
          viewPurchaseOrders: 'Vezi comenzile de achiziție',
          viewSalesOrders: 'Vezi comenzile de vânzare',
        },
      },
      tabs: {
        advanced: {
          label: 'Avansat',
          links: {
            addExternalEvidence: {
              description: 'Adaugă dovezi din surse externe',
              label: 'Adaugă dovezi externe',
            },
            manageMaterialProducts: {
              description: 'Vizualizează și editează produsele de material',
              label: 'Gestionează produsele de material',
            },
            pernType: {
              description: 'Actualizează tipurile PERN',
              label: 'Tip PERN',
            },
          },
        },
        contracts: {
          label: 'Contracte',
        },
        deals: {
          label: 'Tranzacții',
          links: {
            goToDealHub: {
              description: 'Vizualizează și rezolvă sarcinile tranzacțiilor',
              label: 'Mergi la DealHub',
            },
            viewAllDeals: {
              description: 'Vizualizează și editează toate tranzacțiile și încărcăturile',
              label: 'Vezi toate tranzacțiile',
            },
          },
        },
        finance: {
          label: 'Finanțe',
          links: {
            createShippingRate: {
              description: 'Creează un tarif de transport și toate detaliile asociate',
              label: 'Creează tarif de transport',
            },
            manageBanks: {
              description: 'Vizualizează și editează datele bancare',
              label: 'Gestionează băncile',
            },
            manageCosts: {
              description: 'Creează, editează și vizualizează costurile',
              label: 'Gestionează costurile',
            },
            manageExternalCompanies: {
              description: 'Creează, editează și vizualizează datele companiilor externe',
              label: 'Gestionează companiile externe',
            },
            manageFinancing: {
              description: 'Trimite cereri de plată către Finmid',
              label: 'Gestionează finanțarea',
            },
            manageRevenue: {
              description: 'Creează, editează și vizualizează veniturile',
              label: 'Gestionează veniturile',
            },
            marketPernPrices: {
              description: 'Vezi prețurile PERN istorice din T2E',
              label: 'Prețuri PERN de piață',
            },
          },
        },
        support: {
          label: 'Asistență',
          links: {
            getSupport: {
              description: 'Creează o solicitare de asistență în Jira',
              label: 'Obține asistență',
            },
            helpAndLearn: {
              description: 'Ghiduri de ajutor',
              label: 'Ajutor și învățare',
            },
          },
        },
        trade: {
          label: 'Comerț',
          links: {
            generateDocuments: {
              description: 'Generează documente pentru comenzi de achiziție, comenzi de vânzare, expedieri etc.',
              label: 'Generează documente',
            },
            transportMetabase: {
              description: 'Link către Transport Metabase',
              label: 'Transport Metabase',
            },
          },
        },
        usersAndCompanies: {
          label: 'Utilizatori și companii',
          links: {
            copySessions: {
              description: 'Copiază sesiuni în ML Accuracy Testing dintr-un fișier CSV încărcat',
              label: 'Copiază sesiuni',
            },
            createUserAndCompany: {
              description: 'Creează un utilizator și o companie nouă sau invită-l într-o companie existentă',
              label: 'Creează utilizator și companie',
            },
            hijackUser: {
              description: 'Preia rapid contul unui utilizator',
              label: 'Preia un utilizator',
            },
            staffOnboarding: {
              description: 'Creează un utilizator administrator nou și fă-l membru Safi',
              label: 'Integrare personal',
            },
            viewTradeCompanies: {
              description: 'Vizualizează și editează companiile de comerț din cerere și ofertă',
              label: 'Vezi toate companiile de comerț',
            },
          },
        },
      },
    },
    guidePriceChip: {
      label: 'Preț orientativ',
      unavailable: 'Preț orientativ indisponibil',
    },
    helpGuides: {
      aiBale: {
        partOne: {
          boldText: 'Stai la maximum 1 metru',
          imageAlt: 'Stai la maximum 1 metru distanță',
          trailingText: 'în fața grămezii.',
        },
      },
      aiLoose: {
        partFour: {
          and: 'și',
          clearPile: 'Îndepărtează grămada',
          imageAlt: 'Îndepărtează grămada',
          repeatSteps: 'repetă pașii 1-3',
          untilText: 'până când ai făcut câte o fotografie pentru fiecare grămadă din întreaga probă.',
        },
        partOne: {
          imageAlt: 'Fă o grămadă',
          makePile: 'Fă o grămadă',
          middleText: 'din proba ta și întinde-o uniform pe podea.',
          takePhoto: 'Fă 1 fotografie per grămadă.',
        },
        partThree: {
          boldIntro: 'Verifică fotografia înainte de a îndepărta grămada.',
          imageAlt: 'Verifică și șterge',
          trailingText: 'Dacă nu este corectă, o poți șterge și poți face una nouă.',
          warningBold: 'doar 1 fotografie per grămadă',
          warningPrefix: 'Încarcă',
          warningSuffix: 'pentru a evita denaturarea rezultatelor AI.',
        },
      },
      common: {
        completeAlt: 'Finalizat',
      },
      data: {
        guides: {
          aiBale: 'Balot AI',
          aiBriquette: 'Brichetă AI',
          aiLoose: 'Vrac AI',
          emailSupport: 'Scrie-ne la support@safi.co',
        },
        sections: {
          contactSupport: 'Contactează asistența',
          qualityControlGuides: 'Ghiduri de control al calității',
        },
        subcategories: {
          emailSupportPrompt: 'Ai nevoie de asistență sau vrei să soliciți o funcționalitate nouă?',
          howToTakeGreatAiPhotos: 'Cum să faci fotografii AI de calitate',
        },
      },
    },
    manageDataButton: {
      buttonLabel: 'Gestionează datele',
      options: {
        aiBalePhotos: 'Fotografii balot AI',
        loadingEvidence: 'Dovezi de încărcare',
        marketingMedia: 'Materiale de marketing',
        samples: 'Probe',
        siteEvidence: 'Dovezi de la fața locului',
        specifications: 'Specificații',
      },
    },
    materialCategoryActions: {
      notifications: {
        cannotSaveMaterial: 'Materialul nu poate fi salvat',
        errorCreatingMaterial: 'Eroare la crearea materialului',
        errorDeletingMaterial: 'Eroare la ștergerea materialului',
        errorSavingMaterial: 'Eroare la salvarea materialului',
        savedSuccessfully: 'Salvat cu succes',
        selectedTargetGroupHasNoOperands: 'Grupul țintă selectat nu are operanzi. Alege alt grup țintă.',
        skippedSpecifications:
          '{{count}} specificație/specificații nu au fost salvate deoarece nu au operanzi atribuiți.',
      },
    },
    materialComponents: {
      supportedComponentList: {
        buttonText: 'Adaugă componentă de material',
        title: 'Componente de material',
      },
    },
    materialDirtLevel: {
      options: {
        clean: 'Curat',
        dirty: 'Murdar',
        notAvailable: 'N/A',
        veryClean: 'Foarte curat',
      },
    },
    materialProducts: {
      validation: {
        atLeastOneBuyerMaterialComponentIsRequired:
          'Este necesară cel puțin o componentă de material a cumpărătorului.',
        atLeastOneSellerMaterialComponentIsRequired: 'Este necesară cel puțin o componentă de material a vânzătorului.',
        buyerAndSellerMaterialComponentCountsMustMatch:
          'Numărul componentelor de material ale cumpărătorului și ale vânzătorului trebuie să fie identic.',
      },
    },
    materialProductsList: {
      columns: {
        buyerMaterialComponents: 'Componente de material ale cumpărătorului',
        capsAndLabels: 'Capace și etichete',
        country: 'Țară',
        foodGrade: 'Calitate alimentară',
        id: 'ID',
        materialGrade: 'Calitatea materialului',
        materialOrigin: 'Originea materialului',
        materialSpec: 'Specificația materialului',
        process: 'Proces',
        sellerMaterialComponents: 'Componente de material ale vânzătorului',
      },
      values: {
        no: 'Nu',
        notSpecified: 'N/A',
        yes: 'Da',
      },
    },
    notificationParty: {
      label: 'Parte notificată',
      options: {
        allViaAgent: 'Toate comunicările prin agent',
        directWithBuyer: 'Comunică direct cu cumpărătorul',
      },
    },
    orders: {
      validation: {
        fixedPriceAndDateMustBeProvidedTogether: 'Prețul fix și data fixă trebuie completate împreună',
      },
    },
    paymentMethod: {
      label: 'Metodă de plată',
      options: {
        documentAgainstPayment: 'Documente contra plată',
        telegraphicTransfer: 'Transfer bancar',
      },
    },
    percentageLightBlue: {
      label: 'Procent albastru deschis',
      options: {
        notAvailable: 'N/A',
      },
    },
    siteMaterials: {
      notifications: {
        errorCreatingMaterial: 'Eroare la crearea materialului',
        materialCreated: 'Material creat',
        productLinkedSuccessfully: 'Produs asociat cu succes',
      },
    },
    timeFilter: {
      options: {
        last30Days: 'Ultimele 30 de zile',
        last7Days: 'Ultimele 7 zile',
        last90Days: 'Ultimele 90 de zile',
        next14Days: 'Următoarele 14 zile',
        next2Days: 'Următoarele 2 zile',
        next7Days: 'Următoarele 7 zile',
      },
    },
    variablePricingFields: {
      indexOptions: {
        lmeAluminium: 'LME Aluminiu',
        lmeAluminiumMidwestPremium: 'LME Aluminiu + Midwest Premium',
      },
    },
  },
  breakdowns: {
    adminAiVersionTitle: {
      value: 'AI:{{version}}',
    },
    aiSampleBreakdown: {
      analysisNotAvailable: 'Analiză indisponibilă',
    },
    aluMaterialSampleBreakdown: {
      labels: {
        aluminium: 'Aluminiu',
        foilAndTray: 'Folie și caserolă',
        foodCan: 'Conservă alimentară',
        nonAluminium: 'Non-aluminiu',
        nonMetal: 'Non-metal',
        otherAluminium: 'Alt aluminiu',
        otherMetal: 'Alt metal',
        total: 'Total',
        ubc: 'UBC',
      },
    },
    analysisInProgressAlert: {
      partialDescription:
        'Rezultatele de mai jos se bazează pe {{acceptedCount}} din {{submittedCount}} fotografii. Alte {{pendingCount}} sunt încă în curs de analiză.',
      partialTitle: 'Mai multe fotografii sunt în curs de analiză',
      pendingDescription:
        '{{pendingCount}} din {{submittedCount}} fotografii sunt încă în curs de analiză. Rezultatele ar trebui să apară în câteva minute.',
      pendingTitle: 'Analiză în curs',
      photoPendingDescription:
        'Fotografia ta este încă analizată în fundal. Rezultatele și contururile ar trebui să apară în câteva minute.',
      rejectedDescription: '{{rejectedCount}} fotografii au fost respinse. Verifică motivul pe fiecare fotografie.',
    },
    cleanliness: {
      label: 'Curățenie',
      tooltip:
        'Curățenia măsoară cât de curat este materialul. Aceasta variază de la foarte curat, curat, mediu până la murdar. Murdăria include grăsimea, petele și praful prezente pe suprafața materialului.',
    },
    evaluatedSpecsBreakdown: {
      customizeTableFor: 'Personalizează tabelul pentru',
      targetChip: 'Țintă',
      thisMaterial: 'acest material',
      title: 'Analiză AI',
    },
    noAnalysisAlert: {
      description:
        'AI-ul nu a putut analiza fotografiile. Poți verifica motivul pe fiecare fotografie, le poți șterge pe cele respinse și poți încerca din nou.',
      guidance: 'Consultă <guideLink>ghidul {{guideLabel}}</guideLink> pentru sfaturi despre fotografii mai bune.',
      noSubmittedPhotosDescription:
        'Nu a fost trimisă încă nicio fotografie pentru analiză. Adaugă fotografii pentru a începe analiza.',
      title: 'Niciun rezultat disponibil',
    },
    petFlake: {
      fields: {
        bulkDensity: 'Densitate în vrac',
        clearFlakes: 'Fulgi transparenți',
        colouredFlakes: 'Fulgi colorați',
        darkBlueFlakes: 'Fulgi albastru închis',
        ferrousMetal: 'Metal feros',
        glue: 'Adeziv',
        greenFlakes: 'Fulgi verzi',
        intrinsicViscosity: 'Vâscozitate intrinsecă',
        isophthalicAcid: 'Acid izoftalic',
        lightBlueFlakes: 'Fulgi albastru deschis',
        meltFlowRate: 'Indice de curgere la topire (MF) (260°C/2,16Kg)',
        meltingPoint: 'Punct de topire',
        nonFerrousMetal: 'Metal neferos',
        otherImpurities: 'Alte impurități',
        paper: 'Hârtie',
        particlesSizeBetween10And12mm: 'Particule cu dimensiunea între 10 și 12 mm',
        particlesSizeBetween5And10mm: 'Particule cu dimensiunea între 5 și 10 mm',
        particlesSizeLt5mm: 'Particule cu dimensiunea < 5 mm',
        petContent: 'Conținut de polietilen tereftalat (PET)',
        ph: 'pH',
        polyolefinContamination: 'Contaminare cu poliolefine',
        pvcContamination: 'Contaminare cu PVC',
        residualContamination: 'Contaminare reziduală',
        residualMoisture: 'Umiditate reziduală',
        sampledBy: 'Prelevat de',
        woodenMaterials: 'Materiale lemnoase',
        yellowFlakes: 'Fulgi galbeni',
      },
    },
    petMaterialSampleBreakdown: {
      labels: {
        clear: 'Transparent',
        clearFullSleeveNonFoodGrade: 'Transparent cu manșon integral, nealimentar',
        clearNonFoodGrade: 'Transparent, nealimentar',
        clearPetTray: 'Caserolă PET transparentă',
        darkBlue: 'Albastru închis',
        film: 'Folie',
        green: 'Verde',
        largeClear5L: 'Transparent mare (≥5L)',
        lightBlue: 'Albastru deschis',
        liquid: 'Lichid',
        nonPetBottle: 'Sticlă non-PET',
        nonPlastic: 'Non-plastic',
        other: 'Altele',
        otherPlastic: 'Alt plastic',
        petBottle: 'Sticlă PET',
        prohibitive: 'Interzis',
        total: 'Total',
      },
    },
    taintTaborMaterialSampleBreakdown: {
      labels: {
        aluIron: 'Alu./fier',
        aluNonMetal: 'Alu./non-metal',
        aluWithAttachments: 'Alu. cu atașamente',
        aluminium: 'Aluminiu',
        cast: 'Turnat',
        castIron: 'Turnat/fier',
        ferrousMetal: 'Metal feros',
        finesLt20mm: 'Particule fine <20mm',
        nonAluminium: 'non-aluminiu',
        nonMetal: 'Non-metal',
        otherMetal: 'Alt metal',
        prohibitive: 'Interzis',
        radiatorCondenser: 'Radiator/condensator',
        stainlessSteel: 'Oțel inoxidabil',
        taintTabor: 'Taint Tabor',
        total: 'Total',
        ubcFoil: 'UBC/folie',
      },
    },
    zorbaMaterialSampleBreakdown: {
      labels: {
        aluminiumUbc: 'Aluminiu - UBC',
        compositeMaterials: 'Materiale compozite',
        copperBrassRedsYellows: 'Cupru și alamă - „roșii și galbene”',
        finesLt20mm: 'Particule fine <20mm',
        nonFerrousMetalWhites: 'Metal neferos - „albe”',
        nonTarget: 'Non-țintă',
        otherMetalFerrous: 'Alt metal (feros)',
        otherNonMetal: 'Alt material non-metalic',
        postConsumerAluminiumPackaging: 'Ambalaje din aluminiu post-consum',
        prohibitive: 'Interzis',
        targetMaterial: 'Material vizat',
        total: 'Total',
      },
    },
  },
  cameraGuidance: {
    camera: {
      notifications: {
        autoCaptureOff: 'Captură automată dezactivată',
        autoCaptureOn: 'Captură automată activată',
      },
      rejectedPhotos: {
        message:
          'Atinge fotografia de mai jos pentru a vedea motivul erorii, apoi șterge fotografia și încearcă din nou',
        photo: 'fotografie',
        photos: 'fotografii',
        title: '{{prefix}} să verifice {{count}} {{countLabel}}!',
        titlePrefixObfuscated: 'Nu s-a reușit',
        titlePrefixWithAi: 'AI-ul nu a reușit',
      },
    },
    cameraFooter: {
      actions: {
        capture: 'Fotografiază',
        complete: 'Finalizează',
        guide: 'Ghid',
      },
      tooltips: {
        takePhotoToComplete: 'Fă o fotografie pentru a finaliza',
      },
    },
    cameraHeader: {
      autoCapture: 'Captură automată',
      confirmationDialog: {
        actions: {
          complete: 'Finalizează',
          exitAnyway: 'Ieși oricum',
        },
        messageWithAiLabel:
          'Fotografiile tale vor fi salvate, dar nu vei vedea imediat rezultatele AI. Pentru a vedea acum raportul AI, atinge Finalizează.',
        messageWithoutAiLabel:
          'Fotografiile tale vor fi salvate, dar nu vei vedea imediat rezultatele. Pentru a vedea acum raportul, atinge Finalizează.',
        title: 'Ieși din cameră?',
      },
      flashAriaLabel: 'Comută blițul',
      settings: {
        description:
          'Folosește modul offline dacă ai o conexiune de date slabă, pentru a evita pierderea fotografiilor',
        title: 'Setări',
      },
    },
    countdownOverlay: {
      snap: 'Clic!',
    },
    previewImageReview: {
      actions: {
        deleteAndRetake: 'Șterge și refă',
      },
      evidencePhotoAlt: 'Fotografie doveditoare',
      notifications: {
        deleteFailed: 'Fotografia nu a putut fi ștearsă. Încearcă din nou.',
      },
    },
  },
  charts: {
    targetBaleWeightsHistogram: {
      axes: {
        x: '% conținut vizat estimat',
        y: 'Număr de baloturi',
      },
      labels: {
        average: 'Medie: {{targetGrade}}%',
        bales: 'Baloturi',
      },
      tooltip: {
        label: '% detectat: {{label}}',
        value: '{{value}} baloturi',
      },
    },
  },
  dataCards: {
    aluminiumComposition: {
      reportTitle: 'Raport',
      targetDistributionTitle: 'Distribuția materialului vizat',
    },
    common: {
      loads: 'Încărcături',
      metricTonnes: 'Tone metrice',
    },
    hdpeComposition: {
      aiBaleAnalysisTitle: 'Analiză AI a balotului',
      reportTitle: 'Raport',
    },
    loadingDetails: {
      actions: {
        showMedia: 'Afișează media',
      },
      fields: {
        loadingNotes: 'Note de încărcare',
        loadingTechnique: 'Tehnică de încărcare',
        packedOn: 'Ambalat la',
        targetPayload: 'Încărcătură vizată',
      },
      title: 'Detalii de încărcare',
      values: {
        targetPayloadMt: '{{targetPayload}} (MT)',
      },
    },
    materialNotAnalysed: {
      analysisTitle: 'Analiză',
      noResultsDescription:
        'Încă nu am construit un model pentru acest tip specific de material. Însă, cu mai multe date, vom putea oferi analize în viitor.',
      noResultsTitle: 'Niciun rezultat disponibil',
      reportTitle: 'Raport',
    },
    materialSpecifications: {
      actions: {
        showMedia: 'Afișează media',
      },
      analysis: {
        completed: 'Finalizată',
        notCompleted: 'Nefinalizată',
      },
      fields: {
        baleDimensions: 'Dimensiunile balotului',
        capsAndLabels: 'Capace și etichete',
        density: 'Densitate (kg/m³)',
        ferrousCheck: 'Verificare feroasă',
        foodGrade: 'Calitate alimentară',
        materialAnalysisNotes: 'Note privind analiza materialului',
        materialForm: 'Forma materialului',
        materialProcess: 'Procesul materialului',
        origin: 'Origine',
        radiationCheck: 'Verificare radiații',
        strapping: 'Legare',
        visualCheck: 'Verificare vizuală',
      },
      title: 'Specificațiile materialului',
      units: {
        meters: 'm',
      },
      values: {
        no: 'Nu',
        yes: 'Da',
      },
    },
    petComposition: {
      aiBaleAnalysisTitle: 'Analiză AI a balotului',
      reportTitle: 'Raport',
    },
    site: {
      actions: {
        showMedia: 'Afișează media',
      },
      fields: {
        machinesUsed: 'Utilaje folosite',
        materialOrigin: 'Originea materialului',
        monthlyVolume: 'Volum lunar',
        siteGround: 'Solul locației',
        siteNotes: 'Note despre locație',
        storageAndCover: 'Depozitare și acoperire',
      },
      monthlyVolumeValue: '{{productionVolume}} (MT)',
      title: 'Locație',
    },
    tradeCompanySessionLoose: {
      aiLooseAnalysisTitle: 'Analiză AI în vrac',
      reportTitle: 'Raport',
      sampleTotalWeightTitle: 'Greutatea totală a probei',
    },
    transactionHistory: {
      alerts: {
        newSupplier: 'Furnizor nou! Nu ai mai cumpărat de la el până acum.',
        purchasedBefore: 'Ai mai cumpărat de la acest furnizor!',
      },
      title: 'Istoricul tranzacțiilor',
      total: {
        description: 'Tranzacții între acest furnizor și alți cumpărători în ultimele 6 luni.',
      },
    },
  },
  error: {
    actions: {
      contactSupport: 'Contactează asistența',
      goToHomepage: 'Mergi la pagina principală',
      tryAgain: 'Încearcă din nou',
    },
    forbidden: {
      description: 'Nu ai permisiunea de a vedea această pagină.',
      title: 'Acces refuzat',
    },
    generic: {
      description: 'A apărut o eroare neașteptată. Încearcă din nou.',
      title: 'Ceva nu a mers bine',
    },
    network: {
      description: 'Verifică-ți conexiunea la internet și încearcă din nou.',
      title: 'Se pare că ești offline',
    },
    notFound: {
      description: 'Nu am găsit pagina pe care o cauți.',
      title: 'Pagina nu a fost găsită',
    },
    server: {
      description: 'Avem dificultăți în a contacta serverele noastre. Încearcă din nou peste puțin timp.',
      title: 'Ceva nu a mers bine',
    },
    support: {
      body: 'Am întâmpinat o eroare și am nevoie de ajutor.\n\nPagină: {{url}}\nDetalii: {{detail}}\n\nCe făceam:',
      subject: 'Safi Quality — Am nevoie de ajutor cu o eroare',
    },
  },
  evidence: {
    actionBar: {
      deletePrompt: 'Sigur vrei să ștergi dovezile selectate?',
      deleteTitle: 'Șterge {{evidence}}',
      selectedCount: '{{selected}} selectate',
    },
    chips: {
      ai: 'AI',
      selfServe: 'Autoservire',
    },
    defaultBreakdownFormFields: {
      actions: {
        addBreakdownItem: 'Adaugă element de defalcare',
      },
      labels: {
        name: 'Nume',
        unit: 'Unitate',
        value: 'Valoare',
      },
      title: 'Defalcare',
    },
    editForm: {
      actions: {
        delete: 'Șterge',
        edit: 'Editează',
      },
      labels: {
        newEvidenceType: 'Tip nou de {{evidence}}',
        newLabel: 'Nou',
        typeLabel: 'Tip',
      },
      notifications: {
        deleteError: 'Eroare la ștergerea dovezii',
        deleteSuccess: 'Dovadă ștearsă',
        updateError: 'Eroare la actualizarea dovezii',
        updateSuccess: 'Dovadă actualizată',
      },
      validation: {
        typeOrSiteMaterialRequired: 'Trebuie completat fie tipul dovezii, fie materialul locației',
      },
      warnings: {
        deleteMessage:
          'Ești pe cale să ștergi această dovadă din materialul acestei locații. Aceasta va fi eliminată din raportul de calitate asociat. Confirmă pentru a continua.',
        editMessage:
          'Ești pe cale să modifici această dovadă. Aceasta va fi eliminată din raportul de calitate asociat. Confirmă pentru a continua.',
        title: 'Atenție!',
      },
    },
    inspectionSessionDetails: {
      labels: {
        dealId: 'ID tranzacție:',
        inspectionSlug: 'Identificator inspecție:',
      },
      tooltips: {
        reportAvailableOnCompletion: 'După finalizarea inspecției, vei găsi aici un link către raport.',
        searchInDrive: 'Caută inspecția în Google Drive',
        viewDealDetails: 'Vezi detaliile tranzacției',
        viewExternalReport: 'Vezi raportul extern',
      },
    },
    offlineGallery: {
      notifications: {
        cachedClearError: 'Ștergerea fotografiilor din memoria cache a eșuat',
        cachedClearSuccess: 'Fotografiile din memoria cache au fost șterse',
        cachedSyncError: 'Sincronizarea fotografiilor din memoria cache a eșuat',
        cachedSyncSuccess: 'Fotografii încărcate. Analiza poate dura câteva minute.',
        clearError: 'Ștergerea dovezilor offline a eșuat',
        clearSuccess: 'Dovezile offline au fost șterse cu succes',
        missingParentViewCache:
          'Una sau mai multe fotografii din memoria cache aparțin unui raport care nu mai există. Deschide memoria cache pentru a le descărca și reîncărca sau pentru a le șterge.',
      },
      title: '{{evidenceLower}} offline',
      tooltips: {
        cannotSyncWithoutInternet: 'Sincronizarea nu este posibilă fără conexiune la internet',
        clear: 'Șterge dovezile offline',
        sync: 'Sincronizează dovezile offline',
        syncInProgress: 'Sincronizare în curs...',
      },
    },
    sampleBreakdown: {
      labels: {
        aiBreakdown: 'Defalcare AI',
        manualBreakdown: 'Defalcare manuală',
        noNotes: 'Fără note',
        sample: 'Probă',
        sampleEvidence: 'Dovezi ale probei',
      },
      totalWeight: '{{totalWeight}}kg',
    },
    sampleFormFields: {
      breakdowns: {
        alu: {
          groups: {
            aluminium: 'Aluminiu',
            nonAluminium: 'Non-aluminiu',
          },
          items: {
            foilAndTray: 'Folie și caserolă',
            foodCan: 'Conservă alimentară',
            nonMetal: 'Non-metal',
            otherAluminium: 'Alt aluminiu',
            otherAluminiumAerosols: 'Alt aluminiu (aerosoli)',
            otherMetal: 'Alt metal',
            rigidPlastics: 'Plastice rigide',
            ubc: 'UBC',
            vapes: 'Țigări electronice',
          },
        },
        pet: {
          groups: {
            otherPetAndPlastics: 'Alt PET și plastice',
            petBottles: 'Sticle PET',
          },
          items: {
            clear: 'Transparent',
            clearFullSleeve: 'Transparent (manșon integral)',
            clearLarge: 'Transparent (mare)',
            clearPetTray: 'Caserolă PET transparentă',
            darkBlue: 'Albastru închis',
            film: 'Folie',
            green: 'Verde',
            lightBlue: 'Albastru deschis',
            liquid: 'Lichid',
            nonFoodGradeBottle: 'Sticlă PET nealimentară',
            nonPlastic: 'Non-plastic',
            other: 'Altele',
            otherPlastic: 'Alt plastic',
            prohibitive: 'Interzis',
          },
        },
        taintTabor: {
          groups: {
            aluWithAttachments: 'Alu. cu atașamente',
            aluminium: 'Aluminiu',
            nonAluminium: 'non-aluminiu',
          },
          items: {
            aluIron: 'Alu./fier',
            aluNonMetal: 'Alu./non-metal',
            cast: 'Turnat',
            castIron: 'Turnat/fier',
            ferrousMetal: 'Metal feros',
            finesLt20mm: 'Particule fine <20mm',
            nonMetal: 'Non-metal',
            otherMetal: 'Alt metal',
            prohibitive: 'Interzis',
            radiatorCondenser: 'Radiator/condensator',
            stainlessSteel: 'Oțel inoxidabil',
            taintTabor: 'Taint Tabor',
            ubcFoil: 'UBC/folie',
          },
        },
        zorba: {
          groups: {
            nonTarget: 'Non-țintă',
            targetMaterial: 'Material vizat',
          },
          items: {
            aluminiumFoilUbc: 'Aluminiu - folie / UBC',
            compositeMaterials: 'Materiale compozite',
            copperAndBrass: 'Cupru și alamă „roșii și galbene”',
            finesLt20mm: 'Particule fine <20mm',
            nonFerrousMetal: 'Metal neferos „albe”',
            otherMetalFerrous: 'Alt metal (feros)',
            otherNonMetal: 'Alt material non-metalic',
            postConsumerAluminiumPackaging: 'Ambalaje din aluminiu post-consum',
            prohibitive: 'Interzis',
          },
        },
      },
      labels: {
        notes: 'Note',
        totalWeight: 'Greutate totală',
        weightUnit: 'Kg',
      },
    },
    sampleMaterialEditForm: {
      actions: {
        edit: 'Editează',
      },
      warning: {
        editMessage:
          'Ești pe cale să modifici această dovadă. Aceasta va fi eliminată din raportul de calitate asociat. Confirmă pentru a continua.',
        title: 'Atenție!',
      },
    },
    sampleSessionDetails: {
      manualSampleAnalysisTitle: 'Analiză manuală a probei',
      noData: 'Fără date',
      sampleImagesTitle: 'Imaginile probei',
    },
    sessionStats: {
      excludedSubtitle: 'Imaginile nu au trecut regulile noastre de cerințe',
      excludedTitle: 'Excluse',
      includedSubtitle: 'Imaginile au trecut regulile noastre de cerințe',
      includedTitle: 'Incluse',
    },
    unloadingPhotoUploader: {
      label: 'Încarcă fotografii',
      notifications: {
        uploadFailed: 'Încărcarea a eșuat',
        uploadSuccess: 'Încărcare reușită',
      },
    },
  },
  helpGuides: {
    aiBale: {
      partTwo: {
        captions: {
          clearWhichBaleToFocusOn: 'Se vede clar pe care balot trebuie să te concentrezi',
          goodQualityPhoto: 'Fotografie de bună calitate',
          goodQualityPhotoAndDistanceFromBale: 'Fotografie de bună calitate și distanță potrivită față de balot',
          greatDistanceFromBale: 'Distanță excelentă față de balot',
          materialNotTooTightlyPacked: 'Materialul nu este presat prea strâns',
          materialTooFlatAndTightlyPacked: 'Materialul este prea plat și presat prea strâns',
          nearlyAllBaleFaceShown: 'Se vede aproape toată fața balotului',
          photoTooBlurry: 'Fotografia este prea neclară',
          someBackgroundIsOk: 'Puțin fundal este acceptabil',
          tooClose: 'Prea aproape',
          tooManyBalesAngleNotStraightPoorQuality: 'Prea multe baloturi, unghiul nu este drept, calitate slabă',
          tooManyBalesManySidesVisible: 'Prea multe baloturi, se văd multe laturi',
          tooManyBalesTooFarAway: 'Prea multe baloturi, prea departe',
          unclearWhichIsMainBale: 'Nu este clar care este balotul principal',
        },
        intro: 'Iată exemple de fotografii bune și slabe:',
      },
    },
    aiBriquette: {
      partOne: {
        imageAlt: 'Stai la maximum 0,5 metri distanță',
        instructions: '<bold>Stai la 0,5 metri</bold> în fața brichetei.',
      },
      partTwo: {
        analysisInstruction: 'Când analizezi un palet, <bold>fotografiază minimum 6 brichete.</bold>',
        captions: {
          goodDistance: 'Distanță bună',
          markingsVisible: 'Marcajele sunt vizibile',
          noMarkingsVisible: 'Marcajele nu sunt vizibile',
          okDistance: 'Distanță acceptabilă',
          tooDirty: 'Prea murdar',
          tooShredded: 'Prea mărunțit',
        },
        intro: 'Iată exemple de fotografii bune și slabe:',
      },
    },
    aiLoose: {
      partTwo: {
        captions: {
          cannotSeeAllMaterial: 'Nu se vede tot materialul',
          floorHasLargeMarkings: 'Podeaua are marcaje mari',
          materialInsideBlackBox: 'Materialul este în interiorul chenarului negru',
          materialNotOnGroundOrTable: 'Materialul nu este pe podea/masă',
          materialShouldNotTouchPurpleLines: 'Materialul nu trebuie să atingă liniile mov',
          materialSpreadVeryWell: 'Materialul este întins foarte bine',
          materialSpreadWell: 'Materialul este întins bine',
          materialTouchesEdge: 'Materialul atinge marginea',
          shadow: 'Umbră',
          squareFullOfMaterial: 'Pătratul este plin de material',
          tooHeapedAndTooBig: 'Prea îngrămădit și prea mare',
        },
        intro: 'Iată exemple de fotografii bune și slabe:',
      },
    },
    alert: {
      actions: {
        startGuide: 'Începe ghidul - 1 min',
      },
      description: {
        obfuscatedAi: 'Citește acest scurt ghid pentru a face fotografiile potrivite.',
        withAi:
          'Citește acest scurt ghid pentru a face fotografiile potrivite și a obține rezultate AI precise. Fotografii slabe = rezultate slabe.',
      },
      title: 'Folosești {{modelTitle}} pentru prima dată?',
    },
    autoCaptureTip: {
      message:
        'Sfat util! Lucrează mai rapid și păstrează-ți mănușile cu <autoCapture>Captura automată</autoCapture>, care face fotografiile în locul tău.',
    },
    common: {
      actions: {
        back: 'Înapoi',
        finish: 'Termină',
        next: 'Continuă',
        skipGuide: 'Sari peste ghid',
      },
      models: {
        bale: 'balot',
        briquette: 'brichetă',
      },
    },
    complete: {
      message:
        'Când ai terminat, atinge <complete>„Finalizează”</complete> în cameră. Vei vedea apoi <qualityReport>Raportul de calitate.</qualityReport>',
    },
    dialog: {
      stepLabel: 'Pasul {{currentStep}}/{{maxSteps}}',
      title: 'Bine ai venit la {{modelTitle}}!',
    },
    guideTitle: {
      obfuscatedAi: 'Află cum să faci fotografii corect pentru a obține cele mai bune rezultate.',
      withAi: 'Află cum să faci fotografii corect pentru a obține cele mai bune rezultate AI.',
    },
    offlineWarning: {
      message:
        'Sfat util! Activează <offlineMode>Modul offline</offlineMode> pentru a evita pierderea fotografiilor când conexiunea de date este slabă.',
    },
    reviewDeleteHelpText: {
      withAi:
        'Poți vedea dacă un {{model}} a fost <accepted>acceptat</accepted> sau <rejected>respins</rejected> de AI. <delete>Șterge</delete> și <retake>refă</retake> fotografiile incorecte.',
      withoutAi:
        'Poți vedea dacă un {{model}} a fost <accepted>acceptat</accepted> sau <rejected>respins</rejected>. <delete>Șterge</delete> și <retake>refă</retake> fotografiile incorecte.',
    },
    reviewDeleteTip: {
      message:
        'Sfat! Poți de asemenea <reviewDelete>verifica, șterge</reviewDelete> și <addMorePhotos>adăuga mai multe fotografii</addMorePhotos> în Raportul de calitate.',
    },
  },
  instructions: {
    photoInstructionsDrawer: {
      actions: {
        doNotShowAgain: 'Nu mai afișa',
        takePhoto: 'Fă o fotografie',
      },
      description:
        'AI-ul nostru analizează imaginile încărcate după mai mulți parametri. Imaginile tale ar trebui să arate ca mai jos.',
      frontFaceDescription:
        'Cea mai frecventă cauză a unei imagini invalide este orientarea balotului. Obiectele din balot trebuie să fie aplatizate și orientate frontal.',
      frontFaceTitle: 'Ce înseamnă fața frontală?',
      imageAlt: {
        balePhotoExample: 'Exemplu de fotografie a unui balot',
        frontFaceBalePhoto: 'Fotografie cu fața frontală a balotului',
        treeRingBalePhoto: 'Fotografie a unui balot cu aspect de inele de copac',
      },
      labels: {
        objectsFlattenedFaceOn: 'Obiecte aplatizate și orientate frontal',
        objectsSquashedSideOn: 'Obiecte turtite și orientate lateral',
      },
      title: 'Realizarea imaginilor',
    },
  },
  materialComponents: {
    supportedComponent: {
      labels: {
        materialComponent: 'Componentă de material',
      },
    },
    supportedComponentValue: {
      adornments: {
        max: 'Max: ',
        min: 'Min: ',
      },
      labels: {
        booleanValue: 'Valoare booleană',
        value: 'Valoare',
      },
    },
  },
  nativeCachedPhotos: {
    notifications: {
      syncFailed: {
        description:
          'Sunt salvate pe telefon. Deplasează-te într-un loc cu semnal mai bun, apoi sincronizează din nou.',
        message: {
          few: '{{failedCount}} fotografii nu au fost încărcate',
          many: '{{failedCount}} fotografii nu au fost încărcate',
          one: '{{failedCount}} fotografie nu a fost încărcată',
          other: '{{failedCount}} de fotografii nu au fost încărcate',
        },
      },
    },
  },
  navigation: {
    sidebar: {
      closeAriaLabel: 'Închide',
    },
    userMenu: {
      belowSpecLoadEmailAlert: 'Încărcătură sub specificații',
      buyerEvidenceRequestCompletedEmailAlert: 'Invitație de partener finalizată',
      dailyProductionSummary: 'Rezumat zilnic al producției',
      dailyQualityDigest: 'Raport zilnic de calitate',
      emailNotificationsEnabled: 'Notificări activate',
      language: 'Limbă',
      logOut: 'Deconectare',
      manageCompany: 'Gestionează compania',
      notifications: 'E-mailuri de notificare',
      weeklyQualityDigest: 'Raport săptămânal de calitate',
    },
  },
  networkStatus: {
    alert: {
      offlineMessage: 'Ești momentan offline; dovezile vor fi stocate local până revii online.',
    },
    override: {
      label: 'Mod offline',
      notifications: {
        disabled: 'Mod offline dezactivat',
        enabled: 'Mod offline activat',
      },
    },
  },
  offlinePhotosAlert: {
    all: 'toate',
    cacheDialog: {
      close: 'Închide',
      delete: 'Șterge',
      deleteError: 'Ștergerea fotografiei din memoria cache a eșuat',
      deleteMessage: 'Această acțiune va șterge definitiv această fotografie din memoria cache și nu poate fi anulată.',
      deleteSuccess: 'Fotografia din memoria cache a fost ștearsă',
      deleteTitle: 'Șterge fotografia din memoria cache',
      description:
        'Verifică fotografiile din memoria cache, descarcă ce vrei să păstrezi și șterge fotografiile care nu ar trebui sincronizate.',
      download: 'Descarcă',
      downloadUnavailable: 'Fotografia nu este disponibilă pentru descărcare',
      empty: 'Nu s-au găsit fotografii în memoria cache.',
      nativeLoading: 'Se încarcă fotografiile native din memoria cache...',
      orderRef: 'Ref. comandă: {{orderRef}}',
      parentDeleted: 'Raport șters',
      parentUnavailableDescription:
        'Acest lucru se poate întâmpla dacă cineva din compania ta a curățat o sesiune goală dintr-un alt cont. Șterge-le sau descarcă-le și reîncarcă-le în raportul corect.',
      parentUnavailableTitle: 'Aceste fotografii din memoria cache nu mai au un raport',
      parentVerificationFailed: 'Nu s-a putut verifica disponibilitatea raportului. Încearcă din nou mai târziu.',
      previewLoading: 'Se încarcă previzualizarea...',
      previewUnavailable: 'Previzualizare indisponibilă',
      reportId: 'Raport: {{parentId}}',
      supplier: 'Furnizor: {{supplier}}',
      takenAt: 'Realizată la: {{takenAt}}',
      title: 'Fotografii din memoria cache',
    },
    cancel: 'Anulează',
    deleteAction: 'Șterge {{photoNoun}}',
    deleteMessage: 'Această acțiune va șterge definitiv fotografiile și nu poate fi anulată.',
    deleteTitle: 'Șterge fotografiile',
    descriptionWithAi:
      'Sincronizează {{photoNoun}} pentru a activa analiza AI și pentru ca {{objectPronoun}} să fie vizibile pentru ceilalți utilizatori.',
    descriptionWithoutAi:
      'Sincronizează {{photoNoun}} pentru a activa analiza și pentru ca {{objectPronoun}} să fie vizibile pentru ceilalți utilizatori.',
    it: 'aceasta',
    notifications: {
      cachedClearError: 'Ștergerea fotografiilor din memoria cache a eșuat',
      cachedClearSuccess: 'Fotografiile din memoria cache au fost șterse',
      cachedSyncError: 'Sincronizarea fotografiilor din memoria cache a eșuat',
      cachedSyncSuccess: 'Fotografii încărcate. Analiza poate dura câteva minute.',
      clearError: 'Ștergerea fotografiilor offline a eșuat',
      clearSuccess: 'Fotografiile offline au fost șterse',
      syncOfflineEvidenceError: 'Sincronizarea dovezilor offline a eșuat',
      syncPartialFailure: '{{successCount}} fotografii sincronizate, {{failedCount}} eșuate',
      syncSuccess: '{{successCount}} fotografii sincronizate',
      uploadError: 'Încărcarea fotografiilor offline a eșuat',
    },
    photo: 'fotografia',
    photoIs: 'fotografie este',
    photos: 'fotografiile',
    photosAre: 'fotografii sunt',
    syncAction: 'Sincronizează {{photoNoun}}',
    syncingMessage: 'Se sincronizează fotografiile. Poate dura ceva timp...',
    them: 'acestea',
    title: '{{count}} {{pluralVerb}} offline în așteptarea sincronizării',
    viewPhotos: 'Vezi fotografiile',
  },
  omnisearch: {
    noOptions: {
      withValue: 'Niciun rezultat găsit',
      withoutValue: 'Scrie pentru a căuta',
    },
    placeholder: {
      desktop: 'Ctrl/Cmd + K pentru căutare',
      mobile: 'Caută',
    },
  },
  siteMaterial: {
    tradeCompanySiteMaterialPicker: {
      actions: {
        addNewMaterial: 'Adaugă material nou',
        addNewMaterialAriaLabel: 'Adaugă material nou',
      },
      labels: {
        company: 'Companie',
        siteMaterial: 'Materialul locației',
      },
    },
  },
} satisfies I18nDictionaryShape<typeof featuresEnGb>;
